import clsx from "clsx";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Scale } from "lucide-react";
import { api } from "../lib/api";

/**
 * Every rule the stand allocator uses, in one place.
 *
 * "Why this stand?" answers for one aircraft. This answers for the airport: what is
 * absolute, what merely costs something, what each one costs today, and where to go to
 * change it. Everything on the page is read from the live settings as the engine reads
 * them, so it is the engine's own account rather than a description that can go stale.
 */

const card = "rounded-xl border border-line p-4";
const th = "px-2 py-1.5 text-left text-[11px] font-semibold tracking-wide text-ink-3 uppercase";
const td = "px-2 py-1.5 align-top";

export function Rules({ onBack }: { onBack: () => void }) {
  const q = useQuery({ queryKey: ["rules"], queryFn: api.rules });
  const r = q.data;

  return (
    <div className="scroll-thin h-full overflow-auto p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button className="rounded-lg p-1.5 text-ink-2 hover:bg-panel-2" onClick={onBack} aria-label="Back">
          <ArrowLeft className="size-4" />
        </button>
        <h1 className="mr-2 flex items-center gap-2 text-[18px] font-bold">
          <Scale className="size-5 text-ink-3" /> How stands and gates are decided
        </h1>
        {q.isFetching && <Loader2 className="size-4 animate-spin text-ink-3" />}
      </div>
      <p className="mb-4 max-w-3xl text-[13px] text-ink-2">
        Every rule the allocator is using right now, read from the live settings. Hard rules are never broken. Soft rules cost points, and the optimizer looks for the
        arrangement of the whole day that costs least. The numbers here are the ones being charged today; the right-hand column says where to change each one.
      </p>

      {!r ? (
        <p className="text-[13px] text-ink-3">Reading the rules…</p>
      ) : (
        <>
          <Section title="How a day is decided">
            <ol className="ml-5 list-decimal space-y-1 text-[13px] text-ink-2">
              {r.order.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </Section>

          <Section title="Hard rules" sub="Never broken. A plan that would break one is not offered.">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-line">
                  <th className={th}>Rule</th>
                  <th className={clsx(th, "w-72")}>Where to change it</th>
                </tr>
              </thead>
              <tbody>
                {r.hard.map((h) => (
                  <tr key={h.code} className="border-b border-line">
                    <td className={td}>{h.rule}</td>
                    <td className={clsx(td, "text-ink-3")}>{h.from}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Soft rules and what each costs" sub="Points the optimizer tries to avoid. A higher number is avoided harder. For scale: an aircraft left with no stand costs 5,000.">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-line">
                  <th className={th}>Rule</th>
                  <th className={clsx(th, "w-40")}>Cost today</th>
                  <th className={clsx(th, "w-72")}>Where to change it</th>
                </tr>
              </thead>
              <tbody>
                {r.soft.map((s) => (
                  <tr key={s.code} className="border-b border-line">
                    <td className={td}>{s.rule}</td>
                    <td className={clsx(td, "tabular font-semibold")}>{s.cost ?? "—"}</td>
                    <td className={clsx(td, "text-ink-3")}>{s.from}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Airline preferences" sub="Each is a soft rule with its own weight. Learned ones come from where flights were actually parked.">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-line">
                  <th className={th}>Preference</th>
                  <th className={th}>Who</th>
                  <th className={th}>Aircraft</th>
                  <th className={th}>Prefers</th>
                  <th className={clsx(th, "text-right")}>Weight</th>
                </tr>
              </thead>
              <tbody>
                {r.preferences.map((p) => (
                  <tr key={p.id} className="border-b border-line">
                    <td className={clsx(td, "font-semibold")}>
                      {p.label}
                      {p.learned && <span className="ml-1 rounded bg-panel-2 px-1 text-[10px] font-normal text-ink-3">learned</span>}
                    </td>
                    <td className={td}>{p.who}</td>
                    <td className={td}>{p.aircraft}</td>
                    <td className={td}>{p.prefers}</td>
                    <td className={clsx(td, "tabular text-right")}>{p.weight}</td>
                  </tr>
                ))}
                {r.preferences.length === 0 && (
                  <tr>
                    <td className={clsx(td, "text-ink-3")} colSpan={5}>
                      No preferences set.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Section>

          <Section title="Stands and gates" sub="What each stand can take. Blank means no restriction.">
            <div className="scroll-thin overflow-x-auto">
              <table className="w-full min-w-[860px] text-[12.5px]">
                <thead>
                  <tr className="border-b border-line">
                    <th className={th}>Stand</th>
                    <th className={th}>Gate</th>
                    <th className={th}>Kind</th>
                    <th className={th}>Bridge</th>
                    <th className={clsx(th, "text-right")}>Max span</th>
                    <th className={th}>Customs</th>
                    <th className={th}>Airlines</th>
                    <th className={th}>Types</th>
                    <th className={th}>Blocks</th>
                    <th className={th}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {r.stands.map((s) => (
                    <tr key={s.id} className={clsx("border-b border-line", !s.active && "text-ink-3")}>
                      <td className={clsx(td, "font-semibold whitespace-nowrap")}>{s.name}</td>
                      <td className={td}>{s.gate ?? "—"}</td>
                      <td className={td}>{s.kind}</td>
                      <td className={td}>{s.jetbridge ? "Yes" : "—"}</td>
                      <td className={clsx(td, "tabular text-right")}>{s.maxWingspan} m</td>
                      <td className={td}>{s.international === "primary" ? "Primary" : s.international === "fallback" ? "Fallback" : "—"}</td>
                      <td className={td}>{s.allowedAirlines.join(", ") || "—"}</td>
                      <td className={td}>{s.allowedTypes.join(", ") || "—"}</td>
                      <td className={td}>
                        {s.blocks.map((b) => b.text).join("; ") || "—"}
                      </td>
                      <td className={clsx(td, "text-ink-3")}>
                        {[!s.active && "not in service", !s.autoAllocate && "manual use only", s.domesticExcluded && "international only", s.blockedBy.length && `blocked by ${s.blockedBy.map((b) => b.stand).join(", ")}`]
                          .filter(Boolean)
                          .join(" · ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <div className={clsx(card, "mb-4")}>
      <h2 className="text-[14px] font-bold">{title}</h2>
      {sub && <p className="mb-2 text-[12px] text-ink-3">{sub}</p>}
      {!sub && <div className="mb-2" />}
      {children}
    </div>
  );
}
